import { Field, Select } from '@pa/ui'
import { AlertTriangle, RotateCcw, X } from 'lucide-react'
import { useId, useRef } from 'react'
import {
  bodyOf,
  conflictLineOf,
  needsRecipients,
  PF_MESSAGE_CONFLICT_FIELDS,
  PF_MESSAGE_COPY as COPY,
  PF_MESSAGE_FIELDS,
  previewBody,
  renderTemplate,
  subjectOf,
  templateProblem,
  type PfMessages,
} from '../model/pfMessages'
import { useSettingsDraft } from '../SettingsDraftProvider'
import { RecipientPicker } from './RecipientPicker'

type TextKey = 'subject' | 'body' | 'conflictLine'

/**
 * C-14 "PF internal message notification" (task 24, D-81): the PF message staff get for each online booking, shaped
 * like PF's own Messages → New message. **To** is always "In practice" with the people picked here (chips; people
 * removed in PF show a red chip and get nothing); **Regarding patient** is the booked patient; Subject and Message
 * are templates with an Insert field menu and Reset to default; Add to chart and Urgent are always off; a preview
 * renders sample values. Task 26 (D-83): a **Conflict line** (only the three conflict fields), added once per
 * conflict recorded at booking; the preview ends with one sample line. Part of the Settings draft (saved with Save
 * changes).
 */
export function PfMessagesTab() {
  const { draft, update } = useSettingsDraft()
  const pf = draft.pfMessages
  const ids = { to: useId(), subject: useId(), body: useId(), conflictLine: useId() }
  /** The caret of each field when it last lost focus (the Insert field menu takes focus). */
  const caret = useRef<Record<TextKey, number | null>>({ subject: null, body: null, conflictLine: null })

  const setPf = (fn: (p: PfMessages) => PfMessages) => {
    update((d) => ({ ...d, pfMessages: fn(d.pfMessages) }))
  }
  const setPicked = (id: string, isRecipient: boolean) => {
    setPf((p) => ({ ...p, contacts: p.contacts.map((c) => (c.id === id ? { ...c, isRecipient } : c)) }))
  }
  const textOf = (key: TextKey) =>
    key === 'subject' ? subjectOf(pf) : key === 'body' ? bodyOf(pf) : conflictLineOf(pf)
  const setText = (key: TextKey, value: string | null) => {
    setPf((p) => ({ ...p, [key]: value }))
  }
  const insert = (key: TextKey, field: string) => {
    const text = textOf(key)
    const at = caret.current[key] ?? text.length
    const token = `{${field}}`
    setText(key, `${text.slice(0, at)}${token}${text.slice(at)}`)
    caret.current[key] = at + token.length
  }
  const remember = (key: TextKey) => (e: { currentTarget: HTMLInputElement | HTMLTextAreaElement }) => {
    caret.current[key] = e.currentTarget.selectionStart
  }

  const picked = pf.contacts.filter((c) => c.isRecipient)
  const subjectProblem = templateProblem(subjectOf(pf))
  const bodyProblem = templateProblem(bodyOf(pf))
  const conflictProblem = templateProblem(conflictLineOf(pf), 'conflict')
  const fieldOptions = PF_MESSAGE_FIELDS.map((f) => ({ value: f, label: `{${f}}` }))
  const conflictOptions = PF_MESSAGE_CONFLICT_FIELDS.map((f) => ({ value: f, label: `{${f}}` }))
  const conflictErrId = `${ids.conflictLine}-err`

  return (
    <div className="card card-pad set-form pfm">
      <p className="pfm-purpose">{COPY.purpose}</p>
      {needsRecipients(draft) && (
        <div className="warn-box is-danger" role="alert">
          <AlertTriangle aria-hidden />
          <div>
            <p>{COPY.noRecipients}</p>
          </div>
        </div>
      )}

      <Field label={COPY.to} labelId={ids.to}>
        <div className="pfm-to">
          <div className="pfm-scope">
            <Select
              labelledBy={ids.to}
              value="in_practice"
              onChange={() => undefined}
              options={[{ value: 'in_practice', label: COPY.inPractice }]}
              disabled
            />
          </div>
          {pf.contacts.length === 0 ? (
            <p className="hint pfm-empty">{COPY.empty}</p>
          ) : (
            <>
              {picked.length > 0 && (
                <div className="chip-list pfm-chips" role="group" aria-label="Recipients">
                  {picked.map((c) => (
                    <span key={c.id} className={c.removedInPf ? 'mini-chip is-removed' : 'mini-chip'}>
                      <span className="pfm-chip-name">{c.name}</span>
                      {c.removedInPf && <small>{COPY.removedInPf}</small>}
                      <button
                        type="button"
                        aria-label={`Remove ${c.name}`}
                        onClick={() => {
                          setPicked(c.id, false)
                        }}
                      >
                        <X aria-hidden />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <RecipientPicker
                contacts={pf.contacts}
                onPick={(id) => {
                  setPicked(id, true)
                }}
              />
            </>
          )}
        </div>
      </Field>

      <Field label={COPY.regarding}>
        <p className="pfm-static">{COPY.regardingValue}</p>
      </Field>

      {(['subject', 'body'] as const).map((key) => {
        const problem = key === 'subject' ? subjectProblem : bodyProblem
        const id = key === 'subject' ? ids.subject : ids.body
        const errId = `${id}-err`
        const value = textOf(key)
        const common = {
          id,
          className: 'input',
          value,
          'aria-invalid': problem ? true : undefined,
          'aria-describedby': problem ? errId : undefined,
          onBlur: remember(key),
          onSelect: remember(key),
        }
        return (
          <Field key={key} label={key === 'subject' ? COPY.subject : COPY.message} htmlFor={id}>
            {key === 'subject' ? (
              <input
                {...common}
                onChange={(e) => {
                  setText('subject', e.target.value)
                }}
              />
            ) : (
              <textarea
                {...common}
                rows={4}
                onChange={(e) => {
                  setText('body', e.target.value)
                }}
              />
            )}
            {problem && (
              <span className="err-text pfm-err" id={errId}>
                {problem}
              </span>
            )}
            <div className="pfm-tools">
              <div className="pfm-insert">
                <Select
                  label={`${COPY.insertField} (${key === 'subject' ? COPY.subject : COPY.message})`}
                  value={null}
                  placeholder={COPY.insertField}
                  options={fieldOptions}
                  onChange={(f) => {
                    insert(key, f)
                  }}
                />
              </div>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={pf[key] === null}
                onClick={() => {
                  setText(key, null)
                }}
              >
                <RotateCcw aria-hidden /> {COPY.reset}
              </button>
            </div>
          </Field>
        )
      })}

      <Field label={COPY.conflictLine} hint={COPY.conflictHelp} htmlFor={ids.conflictLine}>
        <input
          id={ids.conflictLine}
          className="input"
          value={conflictLineOf(pf)}
          aria-invalid={conflictProblem ? true : undefined}
          aria-describedby={conflictProblem ? conflictErrId : undefined}
          onBlur={(e) => {
            caret.current.conflictLine = e.currentTarget.selectionStart
          }}
          onSelect={(e) => {
            caret.current.conflictLine = e.currentTarget.selectionStart
          }}
          onChange={(e) => {
            setText('conflictLine', e.target.value)
          }}
        />
        {conflictProblem && (
          <span className="err-text pfm-err" id={conflictErrId}>
            {conflictProblem}
          </span>
        )}
        <div className="pfm-tools">
          <div className="pfm-insert">
            <Select
              label={`${COPY.insertField} (${COPY.conflictLine})`}
              value={null}
              placeholder={COPY.insertField}
              options={conflictOptions}
              onChange={(f) => {
                insert('conflictLine', f)
              }}
            />
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={pf.conflictLine === null}
            onClick={() => {
              setText('conflictLine', null)
            }}
          >
            <RotateCcw aria-hidden /> {COPY.reset}
          </button>
        </div>
      </Field>

      <Field label="Options">
        <div className="pfm-checks">
          {[COPY.addToChart, COPY.urgent].map((label) => (
            <label key={label} className="pfm-check">
              <input type="checkbox" checked={false} disabled readOnly /> {label}
            </label>
          ))}
          <span className="hint">{COPY.alwaysOff}</span>
        </div>
      </Field>

      <Field label={COPY.preview}>
        <div className="pfm-preview" role="group" aria-label={COPY.preview}>
          <p className="pfm-preview-subject">{renderTemplate(subjectOf(pf))}</p>
          <p className="pfm-preview-body">{previewBody(pf)}</p>
        </div>
      </Field>
    </div>
  )
}
